import { createApi } from '@reduxjs/toolkit/query/react';

import {
  SupportTicket,
  CreateSupportTicketProps,
  UpdateSupportTicketProps,
  SupportTicketsResponse,
  FeedbackSubmission,
  CreateFeedbackSubmissionProps,
  CreateSupportTicketAPIProps,
  CreateFeedbackSubmissionAPIProps,
} from '@scaleup/models';
import { baseQuery } from './baseQuerySupport';
import { TagTypes } from './tagTypes';

export const supportApi = createApi({
  reducerPath: 'supportApi',
  baseQuery: baseQuery,
  tagTypes: [TagTypes.supportTickets, TagTypes.feedbackSubmissions],
  endpoints: (builder) => ({
    // Get all support tickets for the current user
    getSupportTickets: builder.query<SupportTicketsResponse, string | undefined>({
      query: (exclusiveStartKey?: string) =>
        '/support/tickets' +
        (exclusiveStartKey ? `?exclusiveStartKey=${exclusiveStartKey}` : ''),
      providesTags: [TagTypes.supportTickets],
    }),

    // Get a specific support ticket by ID
    getSupportTicket: builder.query<SupportTicket, string>({
      query: (ticketId: string) => `/support/ticket/${ticketId}`,
      providesTags: [TagTypes.supportTickets],
    }),

    // Create a new support ticket
    createSupportTicket: builder.mutation<SupportTicket, CreateSupportTicketAPIProps>({
      query: (ticketData) => ({
        url: '/support/ticket',
        method: 'POST',
        body: ticketData,
      }),
      invalidatesTags: [TagTypes.supportTickets],
    }),

    // Update an existing support ticket
    updateSupportTicket: builder.mutation<SupportTicket, UpdateSupportTicketProps>({
      query: ({ ticketId, ...updates }) => ({
        url: `/support/ticket/${ticketId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: [TagTypes.supportTickets],
    }),

    // Create a feedback submission
    createFeedbackSubmission: builder.mutation<FeedbackSubmission, CreateFeedbackSubmissionAPIProps>({
      query: (feedbackData) => ({
        url: '/support/feedback',
        method: 'POST',
        body: feedbackData,
      }),
      invalidatesTags: [TagTypes.feedbackSubmissions],
    }),
  }),
});

export const {
  useGetSupportTicketsQuery,
  useGetSupportTicketQuery,
  useCreateSupportTicketMutation,
  useUpdateSupportTicketMutation,
  useCreateFeedbackSubmissionMutation,
} = supportApi;
